import type { FC } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, useTheme } from '@mui/material'
import {
  ArrowForward,
  ChangeHistory,
  CheckBoxOutlineBlank,
  RadioButtonUnchecked,
  Reply,
} from '@mui/icons-material'
import type { DemoStatusIconConfig } from '../data/mockConversationScript'

interface DemoStatusIconProps {
  readonly config: DemoStatusIconConfig
}

export const DemoStatusIcon: FC<DemoStatusIconProps> = ({ config }) => {
  const theme = useTheme()

  if (config.type === 'hidden') {
    return null
  }

  const { Icon, color } = resolveIconAndColor(config.type, theme)
  const iconSx = config.pulse ? pulsingIconSx(color) : staticIconSx(color)

  return (
    <Box sx={iconContainerSx}>
      <Icon sx={iconSx} />
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Icon / color resolution
// ---------------------------------------------------------------------------

interface IconColorPair {
  readonly Icon: typeof ArrowForward
  readonly color: string
}

function resolveIconAndColor(
  type: Exclude<DemoStatusIconConfig['type'], 'hidden'>,
  theme: Theme,
): IconColorPair {
  switch (type) {
    case 'processing':
      return { Icon: ArrowForward, color: theme.palette.primary.main }
    case 'bash_blocked':
      return { Icon: ChangeHistory, color: theme.palette.warning.main }
    case 'question_blocked':
      return { Icon: RadioButtonUnchecked, color: theme.palette.warning.main }
    case 'plan_blocked':
      return { Icon: CheckBoxOutlineBlank, color: '#00acc1' }
    case 'bash_acknowledged':
      return { Icon: ChangeHistory, color: theme.palette.success.main }
    case 'question_acknowledged':
      return { Icon: RadioButtonUnchecked, color: theme.palette.success.main }
    case 'plan_acknowledged':
      return { Icon: CheckBoxOutlineBlank, color: theme.palette.success.main }
    case 'reply':
      return { Icon: Reply, color: theme.palette.success.main }
  }
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const iconContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const pulsingIconSx = (color: string): SxProps<Theme> => ({
  fontSize: '1.75rem',
  color,
  '@media (prefers-reduced-motion: no-preference)': {
    '@keyframes demoPulse': {
      '0%, 100%': { opacity: 1, transform: 'scale(1)' },
      '50%': { opacity: 0.6, transform: 'scale(1.15)' },
    },
    animation: 'demoPulse 2s ease-in-out infinite',
  },
})

const staticIconSx = (color: string): SxProps<Theme> => ({
  fontSize: '1.75rem',
  color,
})
